import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import {
    PACKAGES,
    IMAGES,
    BOOKABLE_SERVICES,
} from "../data/content";
import SectionHeading from "../components/SectionHeading";
import useReveal from "../hooks/useReveal";

// A unified list of all offerings displayed as cards on the overview page.
// Each entry has: slug, eyebrow, name, blurb, meta (short one-line description).
const OFFERINGS = [
    ...BOOKABLE_SERVICES.map((s) => ({
        slug: s.slug,
        eyebrow: s.eyebrow,
        name: s.name,
        blurb: s.short,
        meta: `${s.duration} · One-on-one with Shreya`,
        badge: s.priceLabel,
    })),
    ...PACKAGES.map((p) => ({
        slug: p.slug,
        eyebrow: p.eyebrow,
        name: p.name,
        blurb: p.description,
        meta: p.idealFor,
        badge: null,
    })),
];

export default function PackagesPage() {
    useReveal();
    return (
        <div className="pt-24" data-testid="packages-page">
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                <div className="ssh-reveal">
                    <SectionHeading
                        eyebrow="Signature Programs"
                        title="Deeper containers for"
                        titleItalic="lasting transformation."
                        sub="When a single session is the beginning of something, these multi-week and one-on-one journeys give your healing the time, structure, and depth it deserves."
                    />
                </div>
            </section>

            <section className="bg-[#FAF8F5] border-t border-[#EFEBE4]">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {OFFERINGS.map((o, i) => (
                            <Link
                                to={`/packages/${o.slug}`}
                                key={o.slug}
                                className="group ssh-card p-8 lg:p-10 flex flex-col ssh-reveal"
                                style={{ transitionDelay: `${0.05 * i}s` }}
                                data-testid={`offering-${o.slug}`}
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <p className="ssh-eyebrow">{o.eyebrow}</p>
                                    {o.badge && (
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A9A86] border border-[#8A9A86] px-2 py-1">
                                            {o.badge}
                                        </span>
                                    )}
                                </div>
                                <h2 className="mt-6 font-serif text-2xl lg:text-3xl leading-tight text-[#2C2B29] group-hover:text-[#A68A6B] transition-colors">
                                    {o.name}
                                </h2>
                                <p className="mt-4 text-sm lg:text-base text-[#4A4947] leading-relaxed">
                                    {o.blurb}
                                </p>
                                <p className="mt-6 text-xs italic text-[#75736F] leading-relaxed">
                                    {o.meta}
                                </p>
                                <div className="mt-auto pt-8">
                                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#A68A6B] group-hover:gap-3 transition-all">
                                        Explore program
                                        <ArrowRight
                                            className="h-3.5 w-3.5"
                                            strokeWidth={1.5}
                                        />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-white">
                <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ssh-reveal">
                    <div>
                        <SectionHeading
                            eyebrow="Not sure which to choose?"
                            title="A 30-minute pre-talk"
                            titleItalic="to find your fit."
                            sub="A short, no-pressure conversation with Shreya to listen to where you are and gently suggest a path — ₹555."
                        />
                        <div className="mt-8">
                            <Link to="/contact?service=pre-talk#booking">
                                <Button
                                    className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em]"
                                    data-testid="discovery-call-cta"
                                >
                                    Book a Pre-Talk
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="aspect-[4/3] overflow-hidden">
                        <img
                            src={IMAGES.calmNature}
                            alt="Calm natural setting"
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
